<script setup lang="ts">
import { TrailDSComponents } from '~/utils/builder/init-trail'
import { CommonComponents } from '~/utils/builder/init-common'
import { withoutSelfHostedFonts } from '~/utils/builder/withoutSelfHostedFonts'
import {
  isPreviewing,
  setClientUserAttributes,
  Content,
} from '@builder.io/sdk-vue'
import type { BuilderContent, ComponentInfo } from '@builder.io/sdk-vue'
import type { Component } from 'vue'

type OpenGraphType =
  | 'website'
  | 'article'
  | 'book'
  | 'profile'
  | 'music.song'
  | 'music.album'
  | 'music.playlist'
  | 'music.radio_status'
  | 'video.movie'
  | 'video.episode'
  | 'video.tv_show'
  | 'video.other'
type TwitterCardType = 'summary' | 'summary_large_image' | 'app' | 'player'

interface BuilderSEOFields {
  title?: string
  description?: string
  ogUrl?: string
  ogType?: OpenGraphType
  ogTitle?: string
  ogDescription?: string
  ogImage?: string
  twitterCard?: TwitterCardType
  twitterDomain?: string
  twitterUrl?: string
  twitterTitle?: string
  twitterDescription?: string
  twitterImage?: string
  [key: string]: unknown
}

type BuilderAttributeValue =
  string | number | boolean | string[] | number[] | boolean[] | null | undefined
type TUserAttributes = Record<string, BuilderAttributeValue>

type TProps = {
  model?: string
  content: BuilderContent | null | undefined
  userAttributes?: TUserAttributes
  components?: (ComponentInfo & { component: Component })[]
}

const props = withDefaults(defineProps<TProps>(), {
  model: 'page',
  userAttributes: void 0,
  components: void 0,
})

const config = useRuntimeConfig()
const BUILDER_PUBLIC_API_KEY = config.public.builderio.publicKey

const localeHead = useLocaleHead()
const { localeIsoCode } = useLocaleIso()
const route = useRoute()

const NuxtLinkComponent = resolveComponent('NuxtLink')

const isMounted = ref(false)

onMounted(() => {
  setClientUserAttributes({
    utmCampaign: route.query?.utm_campaign ? `${route.query.utm_campaign}` : '',
    ...(props.userAttributes ? props.userAttributes : {}),
  })

  isMounted.value = true
})

const builderSEOData = computed(() => {
  const dataObject = props.content?.data as BuilderSEOFields | undefined
  return {
    title: dataObject?.title,
    description: dataObject?.description,
    ogUrl: dataObject?.ogUrl,
    ogType: dataObject?.ogType || 'website',
    ogTitle: dataObject?.ogTitle,
    ogDescription: dataObject?.ogDescription,
    ogImage: dataObject?.ogImage,
    twitterCard: dataObject?.twitterCard,
    twitterDomain: dataObject?.twitterDomain,
    twitterUrl: dataObject?.twitterUrl,
    twitterTitle: dataObject?.twitterTitle,
    twitterDescription: dataObject?.twitterDescription,
    twitterImage: dataObject?.twitterImage,
  }
})

// The locale `link` is left out (AP-11190): with a single locale and no prefix
// it is only a canonical, and passed through `useHeadSafe` it replaced every
// other canonical and then never rendered, so no page had one. app.vue sets it.
useHeadSafe({
  htmlAttrs: {
    lang: localeHead.value.htmlAttrs!.lang,
    dir: localeHead.value.htmlAttrs!.dir as 'ltr' | 'rtl' | 'auto' | undefined,
  },
  meta: localeHead.value.meta,
})

useSeoMeta({
  title: () => builderSEOData.value.title,
  description: () => builderSEOData.value.description,
  ogUrl: () => builderSEOData.value.ogUrl,
  ogType: () => builderSEOData.value.ogType,
  ogTitle: () => builderSEOData.value.ogTitle,
  ogDescription: () => builderSEOData.value.ogDescription,
  ogImage: () => builderSEOData.value.ogImage,
  twitterCard: () => builderSEOData.value.twitterCard,
  twitterTitle: () => builderSEOData.value.twitterTitle,
  twitterDescription: () => builderSEOData.value.twitterDescription,
  twitterImage: () => builderSEOData.value.twitterImage,
})

// IBM Plex Sans is self-hosted, so drop the font rules Builder would inject for it
const renderedContent = computed(() =>
  props.content ? withoutSelfHostedFonts(props.content) : undefined
)

const canShowContent = computed(
  () => !!props.content || (isMounted.value && isPreviewing(route.path))
)

const mergedComponents = computed(() => {
  if (props.components) {
    return [...TrailDSComponents, ...CommonComponents, ...props.components]
  }
  return [...TrailDSComponents, ...CommonComponents]
})
</script>

<template>
  <div class="builder-wrapper-view" data-allow-mismatch>
    <Content
      v-if="canShowContent"
      :api-key="BUILDER_PUBLIC_API_KEY"
      api-version="v3"
      :model="props.model"
      :content="renderedContent"
      :custom-components="mergedComponents"
      :data="props.userAttributes"
      :locale="localeIsoCode"
      :link-component="NuxtLinkComponent"
    />
  </div>
</template>

<style>
.builder-fade-enter-active {
  transition: opacity 0.45s cubic-bezier(0.16, 1, 0.3, 1);
  will-change: opacity;
}

.builder-fade-leave-active {
  transition: opacity 0.18s cubic-bezier(0.25, 1, 0.5, 1);
  will-change: opacity;
}

.builder-fade-enter-from,
.builder-fade-leave-to {
  opacity: 0;
}

.builder-wrapper-view {
  width: 100%;
}
</style>
